import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpDown, Zap, Info, ChevronDown, Search } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead, Tabs, Button, Modal, NumberInput } from '../components/UI'
import { CoinIcon } from '../components/Market'
import { useAuth, useMarket, toast } from '../store'
import { get, post } from '../lib/api'
import { clsx, num, fa, pct, changeClass } from '../lib/format'
import { useSeo } from '../lib/seo'

type Currency = { symbol: string; faName: string; color: string }

function CurrencyPicker({
  value, onChange, list, exclude,
}: { value: string; onChange: (v: string) => void; list: Currency[]; exclude?: string }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const cur = list.find((c) => c.symbol === value)
  const rows = list.filter((c) => c.symbol !== exclude && (!q || c.symbol.toLowerCase().includes(q.toLowerCase()) || c.faName.includes(q)))
  return (
    <>
      <button onClick={() => setOpen(true)} className="flex shrink-0 items-center gap-2 rounded-xl border border-white/10 bg-white/[.05] px-3 py-2 transition hover:border-brand-500/40">
        <CoinIcon symbol={cur?.symbol ?? value} color={cur?.color} size={26} />
        <span className="text-sm font-semibold text-white">{cur?.faName ?? value}</span>
        <ChevronDown className="h-4 w-4 text-slate-500" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="انتخاب ارز">
        <div className="relative mb-3">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input className="input pr-10" placeholder="جستجو" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {rows.map((c) => (
            <button
              key={c.symbol}
              onClick={() => { onChange(c.symbol); setOpen(false); setQ('') }}
              className="flex w-full items-center gap-3 rounded-xl p-2.5 text-right transition hover:bg-white/[.06]"
            >
              <CoinIcon symbol={c.symbol} color={c.color} size={32} />
              <div>
                <div className="text-sm text-white">{c.faName}</div>
                <div className="num text-[11px] text-slate-500" dir="ltr">{c.symbol}</div>
              </div>
            </button>
          ))}
        </div>
      </Modal>
    </>
  )
}

export default function Convert() {
  useSeo({
    title: 'خرید سریع و تبدیل ارز دیجیتال',
    description: 'خرید و فروش آنی ارز دیجیتال با تومان و تبدیل مستقیم رمزارزها به یکدیگر، بدون نیاز به دانش معامله‌گری.',
    keywords: 'خرید تتر، فروش بیت کوین، تبدیل ارز دیجیتال',
    canonical: '/convert',
  })
  const { user } = useAuth()
  const tickers = useMarket((s) => s.tickers)
  const [mode, setMode] = useState<'buy' | 'sell' | 'convert'>('buy')
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [from, setFrom] = useState('IRT')
  const [to, setTo] = useState('USDT')
  const [amount, setAmount] = useState('')
  const [quote, setQuote] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [confirm, setConfirm] = useState(false)

  useEffect(() => {
    get('/market/currencies').then((r: any) => {
      setCurrencies([{ symbol: 'IRT', faName: 'تومان', color: '#00E39A' },
        ...r.map((c: any) => ({ symbol: c.symbol, faName: c.faName, color: c.color }))])
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (mode === 'buy') { setFrom('IRT'); setTo((t) => (t === 'IRT' ? 'USDT' : t)) }
    if (mode === 'sell') { setTo('IRT'); setFrom((f) => (f === 'IRT' ? 'USDT' : f)) }
  }, [mode])

  useEffect(() => {
    const a = Number(amount)
    if (!a || from === to) return setQuote(null)
    const id = setTimeout(() => {
      get(`/convert/quote?from_currency=${from}&to_currency=${to}&amount=${a}`)
        .then(setQuote).catch(() => setQuote(null))
    }, 250)
    return () => clearTimeout(id)
  }, [amount, from, to])

  const doConvert = async () => {
    if (!user) return toast.err('برای تبدیل ابتدا وارد حساب کاربری شوید')
    setLoading(true)
    try {
      const r = await post('/convert/execute', { from_currency: from, to_currency: to, amount: Number(amount) })
      toast.ok(`با موفقیت ${num(r.received, 6)} ${to} دریافت کردید`)
      setAmount(''); setQuote(null); setConfirm(false)
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  const quick = [1_000_000, 5_000_000, 10_000_000, 50_000_000]
  const popular = ['USDT', 'BTC', 'ETH', 'SOL', 'TON', 'DOGE']

  return (
    <Shell>
      <SectionHead title="خرید سریع و تبدیل ارز" subtitle="بدون نیاز به دفتر سفارشات، در چند ثانیه معامله کنید" />

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <Card className="!p-6">
          <Tabs
            value={mode}
            onChange={(k) => setMode(k as any)}
            className="mb-6"
            items={[{ key: 'buy', label: 'خرید' }, { key: 'sell', label: 'فروش' }, { key: 'convert', label: 'تبدیل مستقیم' }]}
          />

          <div className="space-y-2">
            <div className="rounded-2xl border border-white/[.07] bg-ink-900/60 p-4">
              <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                <span>پرداخت می‌کنید</span>
                {quote && <span className="num">ارزش: {num(quote.valueIrt)} تومان</span>}
              </div>
              <div className="flex items-center gap-3">
                <input
                  className="num w-full bg-transparent text-2xl font-bold text-white outline-none placeholder:text-slate-600"
                  dir="ltr" inputMode="decimal" placeholder="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                />
                <CurrencyPicker value={from} onChange={setFrom} list={currencies} exclude={to} />
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => { const f = from; setFrom(to); setTo(f); setMode('convert') }}
                className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-ink-800 text-brand-300 transition hover:rotate-180 hover:border-brand-500/50"
              >
                <ArrowUpDown className="h-4 w-4" />
              </button>
            </div>

            <div className="rounded-2xl border border-white/[.07] bg-ink-900/60 p-4">
              <div className="mb-2 text-xs text-slate-500">دریافت می‌کنید</div>
              <div className="flex items-center gap-3">
                <div className="num w-full text-2xl font-bold text-up">{quote ? num(quote.receive, 6) : '0'}</div>
                <CurrencyPicker value={to} onChange={setTo} list={currencies} exclude={from} />
              </div>
            </div>
          </div>

          {from === 'IRT' && (
            <div className="mt-4 flex flex-wrap gap-2">
              {quick.map((v) => (
                <button key={v} onClick={() => setAmount(String(v))} className="chip transition hover:border-brand-500/40 hover:text-brand-200">
                  {num(v)} تومان
                </button>
              ))}
            </div>
          )}

          {quote && (
            <div className="mt-5 space-y-2 rounded-2xl border border-white/[.06] bg-white/[.02] p-4 text-xs">
              {/* قیمت واحد، شفاف: کاربر باید بداند ارزش هر واحد چقدر حساب شده،
                  نه فقط نرخ تبدیل خام */}
              {quote.unitPrice > 0 && (to === 'IRT' || from === 'IRT') && (
                <div className="flex justify-between">
                  <span className="text-slate-500">
                    {to === 'IRT' ? `قیمت خرید ${from} از شما` : `قیمت فروش ${to} به شما`}
                  </span>
                  <span className="num font-semibold text-slate-100">
                    {num(quote.unitPrice)} تومان
                  </span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-slate-500">نرخ تبدیل</span>
                <span className="num text-slate-200">{fa('۱')} {from} = {num(quote.rate, 6)} {to}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">کارمزد ({fa('۰٫۲')}٪)</span>
                <span className="num text-slate-200">{num(quote.fee, 6)} {to}</span></div>
              <div className="flex justify-between border-t border-white/[.06] pt-2">
                <span className="text-slate-400">مبلغ نهایی دریافتی</span>
                <span className="num font-bold text-up">{num(quote.receive, 6)} {to}</span></div>
            </div>
          )}

          <Button className="mt-5 w-full !py-3.5" disabled={!quote} onClick={() => (user ? setConfirm(true) : toast.err('ابتدا وارد حساب کاربری شوید'))}>
            <Zap className="h-4 w-4" />
            {mode === 'buy' ? 'خرید آنی' : mode === 'sell' ? 'فروش آنی' : 'تبدیل آنی'}
          </Button>
          {!user && <Link to="/login" className="btn-ghost mt-2 w-full">ورود / ثبت‌نام</Link>}

          <p className="mt-4 flex items-start gap-2 text-[11px] leading-6 text-slate-500">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            نرخ نمایش‌داده‌شده تا {fa('۱۲')} ثانیه معتبر است. حداقل مبلغ هر تبدیل {fa('۱۰۰٬۰۰۰')} تومان است.
          </p>
        </Card>

        <div className="space-y-5">
          <Card>
            <h3 className="mb-4 font-bold text-white">ارزهای محبوب</h3>
            <div className="space-y-2">
              {popular.map((s) => {
                const t = tickers[`${s}IRT`]
                if (!t) return null
                return (
                  <button
                    key={s}
                    onClick={() => { setMode('buy'); setFrom('IRT'); setTo(s) }}
                    className="flex w-full items-center gap-3 rounded-xl p-2 text-right transition hover:bg-white/[.05]"
                  >
                    <CoinIcon symbol={t.base} color={t.color} size={32} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-white">{t.faName}</div>
                      <div className="num text-[11px] text-slate-500">{num(t.last)} تومان</div>
                    </div>
                    <span className={clsx('num text-xs font-semibold', changeClass(t.change))}>{pct(t.change)}</span>
                  </button>
                )
              })}
            </div>
          </Card>

          <Card>
            <h3 className="mb-3 font-bold text-white">چرا تبدیل سریع؟</h3>
            <ul className="space-y-3 text-xs leading-6 text-slate-400">
              <li>• بدون نیاز به تحلیل دفتر سفارشات، معامله در یک کلیک</li>
              <li>• قیمت شفاف و بدون کارمزد پنهان</li>
              <li>• امکان تبدیل مستقیم رمزارز به رمزارز</li>
              <li>• تسویه آنی و واریز فوری به کیف پول</li>
            </ul>
          </Card>
        </div>
      </div>

      <Modal open={confirm} onClose={() => setConfirm(false)} title="تأیید نهایی معامله">
        {quote && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/[.07] bg-ink-900/60 p-4 text-sm">
              <div className="mb-2 flex justify-between"><span className="text-slate-500">پرداخت</span>
                <span className="num text-white">{num(Number(amount), 6)} {from}</span></div>
              <div className="mb-2 flex justify-between"><span className="text-slate-500">دریافت</span>
                <span className="num text-up">{num(quote.receive, 6)} {to}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">کارمزد</span>
                <span className="num text-slate-300">{num(quote.fee, 6)} {to}</span></div>
            </div>
            <Button loading={loading} onClick={doConvert} className="w-full !py-3">تأیید و انجام معامله</Button>
          </div>
        )}
      </Modal>
    </Shell>
  )
}
